import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "cardlon:pwa-install-dismissed";

/**
 * InstallPWA — small floating banner that prompts the user to install Cardlon
 * as a Progressive Web App. Listens to the `beforeinstallprompt` event
 * (Chrome/Edge/Android). On other browsers the component renders nothing.
 *
 * The user can dismiss the prompt for the session (LocalStorage flag).
 * If the app is already running standalone (installed), we hide ourselves.
 */
export function InstallPWA() {
  const [event, setEvent] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [show, setShow] = useState(false);

  // Hide if already installed (running in standalone display mode)
  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      (window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone)
    ) {
      return; // already installed → nothing to do
    }

    try {
      const v = window.localStorage.getItem(DISMISS_KEY);
      if (v) {
        const ts = parseInt(v, 10);
        // Re-show after 14 days
        if (!Number.isNaN(ts) && Date.now() - ts < 1000 * 60 * 60 * 24 * 14) {
          setDismissed(true);
          return;
        }
      }
    } catch (e) {
      /* ignore */
    }

    const handler = (e) => {
      e.preventDefault();
      setEvent(e);
      // Delay reveal so it doesn't immediately interrupt initial UX
      setTimeout(() => setShow(true), 4500);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (e) {
      /* ignore */
    }
    setDismissed(true);
    setShow(false);
  };

  const install = async () => {
    if (!event) return;
    try {
      await event.prompt();
      const choice = await event.userChoice;
      if (choice?.outcome === "accepted") {
        setShow(false);
      } else {
        dismiss();
      }
    } catch (e) {
      dismiss();
    }
  };

  if (!event || dismissed || !show) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-[200] max-w-sm rounded-[var(--radius)] border border-[var(--gold)]/40 bg-[var(--surface-1)] shadow-2xl p-4 animate-in fade-in slide-in-from-bottom-2"
      role="dialog"
      aria-label="App installieren"
      data-testid="pwa-install-prompt"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-2 right-2 text-[var(--text-3)] hover:text-[var(--text-1)] p-1.5 rounded-md hover:bg-[var(--surface-2)]"
        aria-label="Schließen"
        data-testid="pwa-install-dismiss"
      >
        <X size={14} />
      </button>
      <div className="flex items-start gap-3 pr-4">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[var(--gold)]/15 shrink-0">
          <Download size={18} className="text-[var(--gold)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm text-[var(--text-1)] mb-0.5">Cardlon installieren</div>
          <p className="text-[12px] text-[var(--text-3)] leading-snug">
            Schneller laden, Home-Screen-Icon und Vollbild-Erlebnis — wie eine native App.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3 justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={dismiss}
          data-testid="pwa-install-later"
        >
          Später
        </Button>
        <Button
          size="sm"
          onClick={install}
          className="bg-[var(--gold)] hover:bg-[var(--gold-2)] text-black font-bold"
          data-testid="pwa-install-accept"
        >
          <Download size={12} className="mr-1.5" />
          Installieren
        </Button>
      </div>
    </div>
  );
}
